import React, { useEffect, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { api } from "@/lib/api";

function MultiPick({ label, all, items, value, onChange, testId }) {
  const isAll = value === "all";
  const list = isAll ? [] : value;
  return (
    <div className="rounded-lg border border-border p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <Label className="mb-0">{label}</Label>
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={isAll} onCheckedChange={(v) => onChange(v ? "all" : [])} data-testid={`${testId}-all`} /> {all}</label>
      </div>
      {!isAll && (
        <div className="mt-2 max-h-36 space-y-1 overflow-y-auto">
          {items.length === 0 && <p className="text-xs text-muted-foreground">None available</p>}
          {items.map((it) => (
            <label key={it.value} className="flex items-center gap-2 text-sm">
              <Checkbox checked={list.includes(it.value)} onCheckedChange={(v) => onChange(v ? [...list, it.value] : list.filter((x) => x !== it.value))} data-testid={`${testId}-opt-${it.value}`} />
              {it.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DpPick({ value, onChange }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!q) { setRes([]); return; }
    const t = setTimeout(() => api.get("/dp/search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const add = (d) => { if (!value.includes(d.id)) onChange([...value, d.id]); setNames((n) => ({ ...n, [d.id]: `${d.full_name} (${d.fe_id})` })); setQ(""); };
  return (
    <div className="rounded-lg border border-border p-3 sm:col-span-2" data-testid="scope-dps">
      <Label>Specific Delivery Partners <span className="text-xs font-normal text-muted-foreground">(optional — restricts to only these DPs)</span></Label>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, FE ID or phone" data-testid="scope-dp-search" />
      {res.length > 0 && <div className="mt-1 max-h-32 overflow-y-auto rounded border border-border">{res.map((d) => (
        <button key={d.id} type="button" onClick={() => add(d)} className="block w-full px-2 py-1 text-left text-sm hover:bg-accent" data-testid={`scope-dp-opt-${d.fe_id}`}>{d.full_name} · {d.fe_id}</button>))}</div>}
      <div className="mt-2 flex flex-wrap gap-1">{value.map((id) => (
        <span key={id} className="flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">{names[id] || id.slice(0, 8)}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== id))}><X className="h-3 w-3" /></button></span>))}</div>
    </div>
  );
}

export function ScopePicker({ meta, scope, onChange }) {
  const set = (k, v) => onChange({ ...scope, [k]: v });
  const locs = (meta?.locations || []).map((l) => ({ value: l.name, label: l.name }));
  const frs = (meta?.franchises || []).map((f) => ({ value: f.id, label: `${f.name} (${f.referral_number})` }));
  return (
    <div className="mt-2">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Access Scope</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MultiPick label="Locations" all="All Locations" items={locs} value={scope.locations} onChange={(v) => set("locations", v)} testId="scope-locations" />
        <MultiPick label="Franchises" all="All Franchises" items={frs} value={scope.franchises} onChange={(v) => set("franchises", v)} testId="scope-franchises" />
        <DpPick value={scope.dp_ids || []} onChange={(v) => set("dp_ids", v)} />
      </div>
    </div>
  );
}
